import * as React from 'react'
import type {
  TreeManager,
  TreeManagerMethods,
  TreeNode,
} from '@basehub/utils/tree/index'
import {
  isTypeForBlockWithChildren,
  BlockChange,
  Block,
  isBlockWithChildren,
  isRootBlock,
  isComponentBlock,
  ContentRequest,
  AtLeast,
  isInstanceBlock,
  BlockMilestone,
  isListBlock,
  isAgentBlock,
} from '@basehub/data/replicache/index'
import * as TreePrimitive from '~/sotano-kit/primitives/tree'
import { BlockIcon } from '~/components/block-icon'
import {
  EnterIcon,
  MinusIcon,
  PlusIcon,
  ResetIcon,
} from '@radix-ui/react-icons'
import { SimpleTooltip } from '~/sotano-kit/primitives/tooltip'
import { getBlockDisplayData } from '@basehub/data/replicache/mutators/repo-space/block/helpers/component-helpers'
import { clsx } from 'clsx'
import type { StageControls } from '~/hooks/use-stage-block'
import { shouldShowChangeInSidebar } from '~/workers/common'
import { StatusIcon } from '~/components/icons/status'
import { BlockIconStoreProvider } from '~/components/block-icon/store'
import { idToSourceBlockId } from '@basehub/data/replicache/mutators/repo-space/block/mutators/copy-on-write'
import { ContributorsHug } from '../../../history/_components/sidebar/primitive-commit'
import { useCustomSyncerSub } from '~/replicache/frontend/use-custom-syncer-sub'
import { useRepoReplicache } from '~/hooks-data/use-repo-replicache'
import { flattenContributors } from '@basehub/data/replicache/schema/block-milestone/util'

type ChangesTreeProps = {
  changesTreeManager: TreeManager<BlockChange, NonNullable<unknown>>
  treeNew: TreeManager<Block, NonNullable<unknown>>
  treeOld: TreeManager<Block, NonNullable<unknown>> | null
  activeKey: { label: string; id: string } | null
  onActiveKeyChange: (
    key: string,
    opts: { autoFocus: boolean; partiallyOpen: boolean }
  ) => void
  onOpenBlock?: (blockId: string) => void
  staging?: { stageControls: StageControls; show: 'staged' | 'unstaged' }
  selectedKeys: Set<{ label: string; id: string }>
  setSelectedKeys: React.Dispatch<
    React.SetStateAction<Set<{ label: string; id: string }>>
  >
  className?: string
  autoFocusKey: string | null
  changesCount: number
  hasTitle?:
    | boolean
    | {
        title: string
      }
  onDiscardChanges?: () => void
  cr?: ContentRequest | null
  defaultSelectFirst?: boolean
  rootEmptyStateMessage?: React.ReactNode
}

export const ChangesTree = ({
  className,
  changesTreeManager,
  treeNew,
  treeOld,
  activeKey,
  onActiveKeyChange,
  selectedKeys,
  setSelectedKeys,
  onOpenBlock,
  staging,
  hasTitle,
  autoFocusKey,
  onDiscardChanges,
  cr,
  defaultSelectFirst,
  rootEmptyStateMessage,
}: ChangesTreeProps) => {
  const onOpenBlockRef = React.useRef(onOpenBlock)
  // eslint-disable-next-line react-compiler/react-compiler
  onOpenBlockRef.current = onOpenBlock

  const { data } = useRepoReplicache()
  const replicache = data?.replicache

  const treeNewMethods = React.useMemo(() => {
    return {
      getChildrenKeys: treeNew.getChildrenKeys,
      getNode: treeNew.getNode,
      getFlatSortedTree__EXPENSIVE: treeNew.getFlatSortedTree__EXPENSIVE,
      getPath: treeNew.getPath,
      addNode: treeNew.addNode,
      root: treeNew.root,
      compoundIndexes: treeNew.compoundIndexes,
      updateNode: treeNew.updateNode,
      deleteNode: treeNew.deleteNode,
      getKey: treeNew.getKey,
      getNodeBy: treeNew.getNodeBy,
      getOrder: treeNew.getOrder,
      getValue: treeNew.getValue,
      lastUpdatedAt: treeNew.lastUpdatedAt,
      getAllNodes: treeNew.getAllNodes,
    } satisfies TreeManagerMethods<Block, NonNullable<unknown>>
  }, [
    treeNew.getChildrenKeys,
    treeNew.getNode,
    treeNew.getFlatSortedTree__EXPENSIVE,
    treeNew.getPath,
    treeNew.addNode,
    treeNew.root,
    treeNew.compoundIndexes,
    treeNew.updateNode,
    treeNew.deleteNode,
    treeNew.getKey,
    treeNew.getNodeBy,
    treeNew.getOrder,
    treeNew.getValue,
    treeNew.getAllNodes,
    treeNew.lastUpdatedAt,
  ])

  const treeOldMethods = React.useMemo(() => {
    if (!treeOld?.getChildrenKeys) return null
    return {
      getChildrenKeys: treeOld.getChildrenKeys,
      getNode: treeOld.getNode,
      getFlatSortedTree__EXPENSIVE: treeOld.getFlatSortedTree__EXPENSIVE,
      getPath: treeOld.getPath,
      addNode: treeOld.addNode,
      root: treeOld.root,
      compoundIndexes: treeOld.compoundIndexes,
      updateNode: treeOld.updateNode,
      deleteNode: treeOld.deleteNode,
      getKey: treeOld.getKey,
      getNodeBy: treeOld.getNodeBy,
      getOrder: treeOld.getOrder,
      getValue: treeOld.getValue,
      lastUpdatedAt: treeOld.lastUpdatedAt,
      getAllNodes: treeOld.getAllNodes,
    } satisfies TreeManagerMethods<Block, NonNullable<unknown>>
  }, [
    treeOld?.getChildrenKeys,
    treeOld?.getNode,
    treeOld?.getFlatSortedTree__EXPENSIVE,
    treeOld?.getPath,
    treeOld?.addNode,
    treeOld?.root,
    treeOld?.compoundIndexes,
    treeOld?.updateNode,
    treeOld?.deleteNode,
    treeOld?.getKey,
    treeOld?.getNodeBy,
    treeOld?.getOrder,
    treeOld?.getValue,
    treeOld?.getAllNodes,
    treeOld?.lastUpdatedAt,
  ])

  const Children = React.useCallback(
    ({ child, childrenToRender }: TreePrimitive.ChildProps<BlockChange>) => {
      if (
        !isTypeForBlockWithChildren(child.value.type) &&
        child.value.type !== 'agent'
      ) {
        return null
      }

      let title = child.value.title
      let highlight = false
      let dim = false

      let node: null | TreeNode<Block> = null
      let parentNode: null | TreeNode<Block> = null
      if (child.value.status === 'deleted') {
        // look in the treeOld
        node = treeOldMethods?.getNode(child.value.id) ?? null
        parentNode = node?.parentKey
          ? (treeOldMethods?.getNode(node.parentKey) ?? null)
          : null
        if (node && parentNode) {
          const result = getBlockDisplayData({
            block: node.value,
            parentBlock: parentNode.value,
            ignoreDisplayTitle: true,
          })
          title = result.displayTitle
          highlight = isComponentBlock(node.value)
          dim = node.value.hidden
        }
      } else {
        // look in the treeNew
        node = treeNewMethods.getNode(child.value.id)
        parentNode = node?.parentKey
          ? treeNewMethods.getNode(node.parentKey)
          : null
        if (node && parentNode) {
          const result = getBlockDisplayData({
            block: node.value,
            parentBlock: parentNode.value,
            ignoreDisplayTitle: true,
          })
          title = result.displayTitle
          highlight = isComponentBlock(node.value)
          dim = node.value.hidden
        }
      }

      const inheritsStaged = staging?.stageControls.getInheritsStage(
        child.value.id
      )
      const isStaged = staging?.stageControls.getIsStaged(child.value.id)
      const conflictResolution = cr?.conflictResolutions?.[child.value.id]

      if (staging?.show === 'staged' && !isStaged) return null
      if (staging?.show === 'unstaged' && isStaged) return null

      return (
        <TreePrimitive.TreeListItem rename="disabled">
          <TreePrimitive.TreeListItemChevron
            saveExpandedStateInStorage={{
              type: 'sessionStorage',
              key: child.key,
            }}
            disabled={!childrenToRender?.length}
            defaultExpanded={
              child.value.status !== 'deleted' &&
              child.value.status !== 'created'
            }
          />
          <TreePrimitive.TreeListItemIcon
            className={clsx(
              highlight && `text-dark-control-highlight`,
              dim && (highlight ? 'opacity-70' : 'opacity-50')
            )}
          >
            <BlockIcon
              block={
                {
                  id: child.key,
                  type: child.value.type,
                  value:
                    child.value.current?.value ?? child.value.previous?.value,
                } as AtLeast<Block, 'id' | 'type' | 'value'>
              }
            />
          </TreePrimitive.TreeListItemIcon>
          <TreePrimitive.TreeListItemTitle
            rename="disabled"
            className={clsx(
              highlight && `text-dark-control-highlight`,
              dim && (highlight ? 'opacity-70' : 'opacity-50')
            )}
          >
            {title}
          </TreePrimitive.TreeListItemTitle>

          {onOpenBlockRef.current && child.value.status !== 'deleted' && (
            <TreePrimitive.TreeListItemActions>
              <TreePrimitive.TreeListItemAction
                ariaLabel="Open block"
                icon={<EnterIcon />}
                onClick={() => {
                  onOpenBlockRef.current?.(child.value.id)
                }}
              />
            </TreePrimitive.TreeListItemActions>
          )}

          <TreePrimitive.TreeListItemLabel>
            <StatusIcon
              status={child.value.status}
              className="size-[15px]"
              isConflictResolved={!!conflictResolution}
              blameProps={{
                sourceBlockId: idToSourceBlockId(child.value),
                rootId: treeNew.root.value.id,
              }}
            />
          </TreePrimitive.TreeListItemLabel>

          {staging?.stageControls.enabled && (
            <TreePrimitive.TreeListItemLabel>
              <SimpleTooltip
                content={
                  inheritsStaged
                    ? 'Inherits from parent'
                    : isStaged
                      ? 'Unstage block'
                      : 'Stage block'
                }
              >
                <div className="flex items-center">
                  <span
                    className={TreePrimitive.$actionButton()}
                    onDoubleClick={(e) => e.stopPropagation()}
                    onContextMenu={(e) => e.stopPropagation()}
                    data-disabled={inheritsStaged}
                    aria-disabled={inheritsStaged}
                    onClick={(e) => {
                      e.stopPropagation()
                      if (inheritsStaged) return
                      staging.stageControls?.stageBlock(child.value.id)
                    }}
                    aria-label={isStaged ? 'Unstage block' : 'Stage block'}
                  >
                    {isStaged ? <MinusIcon /> : <PlusIcon />}
                  </span>
                </div>
              </SimpleTooltip>
            </TreePrimitive.TreeListItemLabel>
          )}
        </TreePrimitive.TreeListItem>
      )
    },
    [
      cr?.conflictResolutions,
      staging?.show,
      staging?.stageControls,
      treeNew.root.value.id,
      treeNewMethods,
      treeOldMethods,
    ]
  )

  const blockIds = React.useMemo(
    () => staging?.stageControls.filterBlockIdsByStage(staging.show) ?? [],
    [staging]
  )

  const deepBlockIds = React.useMemo(
    () =>
      staging?.stageControls.filterBlockIdsByStage(staging.show, {
        includeRoot: false,
        includeNested: true,
      }) ?? [],
    [staging]
  )

  const sourceBlockIdsForMilestonesQuery = React.useMemo(() => {
    if (!treeNewMethods) return []
    if (!staging?.show) return []
    if (staging.show === 'unstaged') return []

    function getChildrenKeysRecursively(blockIds: string[]): string[] {
      const result = []
      for (const key of blockIds) {
        const node = treeNewMethods.getNode(key)
        // Only add document-like blocks, since primitives propagate their contributors
        if (
          node?.value &&
          isBlockWithChildren(node.value) &&
          // but not lists as lists can be large
          !isListBlock(node.value)
        ) {
          result.push(idToSourceBlockId(node.value))
          const keys = treeNewMethods.getChildrenKeys(key)
          result.push(...getChildrenKeysRecursively(keys))
        }
      }

      return result
    }

    return getChildrenKeysRecursively(blockIds)
  }, [blockIds, staging?.show, treeNewMethods])

  const { data: milestones } = useCustomSyncerSub(replicache, {
    syncer: 'multiBlockMilestones',
    params: {
      rootId: treeNew.root.value.id,
      sourceBlockIds: sourceBlockIdsForMilestonesQuery,
    },
  })

  const contributors = React.useMemo(() => {
    let allContributors: BlockMilestone['contributors'] = {}
    milestones?.forEach((m) => {
      allContributors = { ...allContributors, ...m.contributors }
    })

    return flattenContributors(allContributors)
  }, [milestones])

  const shouldForceExpanded = React.useCallback(
    (key: string) => {
      if (staging?.show !== 'staged') return false
      const node = changesTreeManager.getNode(key)
      if (!node || isInstanceBlock(node.value)) return false

      const isStaged = staging.stageControls.getIsStaged(key)
      const children = changesTreeManager
        .getChildrenKeys(key)
        .filter((childId) => {
          const child = changesTreeManager.getNode(childId)?.value
          return child && isBlockWithChildren(child)
        })
      return !isStaged && children.length > 0
    },
    [changesTreeManager, staging?.show, staging?.stageControls]
  )

  return (
    <BlockIconStoreProvider
      getChildrenKeys={(id) => {
        try {
          return treeNewMethods.getChildrenKeys(id)
        } catch {
          return treeOldMethods?.getChildrenKeys(id) ?? []
        }
      }}
    >
      <TreePrimitive.Tree
        treeKey="changes-tree"
        className={className}
        treeManager={changesTreeManager as TreeManager<Block | BlockChange>}
        activeKey={activeKey}
        onActiveKeyChange={({ id }, { doubleClick }) => {
          onActiveKeyChange(id, {
            autoFocus: false,
            partiallyOpen: !doubleClick,
          })
        }}
        selectedKeys={selectedKeys}
        setSelectedKeys={setSelectedKeys}
        dragAndDrop="disabled"
        autoFocusKey={autoFocusKey}
      >
        {hasTitle && (
          <TreePrimitive.TreeTitle
            nestedItemsQuantity={deepBlockIds.length}
            preActions={
              <>
                {contributors && contributors.length > 0 && (
                  <ContributorsHug
                    contributors={contributors}
                    size="petite"
                    max={2}
                  />
                )}
                {onDiscardChanges && (
                  <SimpleTooltip
                    content={
                      blockIds.length === 0
                        ? 'Nothing to discard'
                        : 'Discard changes'
                    }
                  >
                    <span className="flex">
                      <span
                        className={TreePrimitive.$actionButton()}
                        role="button"
                        aria-label="discard changes"
                        data-disabled={blockIds.length === 0}
                        aria-disabled={blockIds.length === 0}
                        onClick={(e) => {
                          e.stopPropagation()
                          onDiscardChanges()
                        }}
                      >
                        <ResetIcon />
                      </span>
                    </span>
                  </SimpleTooltip>
                )}
              </>
            }
            showActionsOnHover={false}
            postActions={
              staging?.stageControls.enabled && (
                <SimpleTooltip
                  content={
                    staging?.show === 'staged'
                      ? 'Unstage all changes'
                      : 'Stage all changes'
                  }
                >
                  <div className="flex items-center !ml-1.5">
                    <span
                      className={TreePrimitive.$actionButton()}
                      role="button"
                      onClick={(e) => {
                        e.stopPropagation()
                        if (staging?.show === 'staged') {
                          staging.stageControls.unstageAll()
                        } else {
                          staging.stageControls.stageAll()
                        }
                      }}
                      onDoubleClick={(e) => e.stopPropagation()}
                      onContextMenu={(e) => e.stopPropagation()}
                      aria-label={
                        staging?.show === 'staged'
                          ? 'Unstage all changes'
                          : 'Stage all changes'
                      }
                    >
                      {staging?.show === 'staged' ? (
                        <MinusIcon />
                      ) : (
                        <PlusIcon />
                      )}
                    </span>
                  </div>
                </SimpleTooltip>
              )
            }
          >
            {typeof hasTitle === 'object' ? hasTitle.title : 'Changes'}
          </TreePrimitive.TreeTitle>
        )}
        <TreePrimitive.TreeList>
          <TreePrimitive.TreeListChild
            shouldForceExpanded={shouldForceExpanded}
            parent={changesTreeManager.root}
            getChildrenToRender={(parent) => {
              function processNode(
                node: TreeNode<BlockChange>
              ): null | Array<TreeNode<BlockChange>> {
                if (
                  !isRootBlock((node.value.current ?? node.value.previous)!) &&
                  !isBlockWithChildren(
                    (node.value.current ?? node.value.previous)!
                  ) &&
                  !isAgentBlock(node.value.current ?? node.value.previous!)
                ) {
                  return null
                }

                const children =
                  changesTreeManager.getChildrenKeys(node.value.id) ?? []
                const processedChildren: Array<TreeNode<BlockChange>> = []

                children.reduce((acc, childId) => {
                  const childNode = changesTreeManager.getNode(childId, {
                    required: true,
                  })

                  // let thisChildWasReorderedFromThisBlock = false
                  // if (childNode.value.status === 'reordered') {
                  //   const current = node.value.current as
                  //     | RootBlock
                  //     | BlockWithChildren
                  //     | undefined
                  //   if (current) {
                  //     const currentChildren = treeNewMethods.getChildrenKeys(
                  //       current.id
                  //     )
                  //     thisChildWasReorderedFromThisBlock = currentChildren.some(
                  //       (currentChildId) => {
                  //         return currentChildId === childNode.value.current?.id
                  //       }
                  //     )
                  //   }
                  // }
                  // if (thisChildWasReorderedFromThisBlock) return acc
                  const shouldShowInSidebar = shouldShowChangeInSidebar(
                    childNode.value,
                    node.value
                  )
                  if (shouldShowInSidebar) acc.push(childNode)
                  else {
                    const processed = processNode(childNode)
                    if (processed) {
                      acc.push(...processed)
                    }
                  }
                  return acc
                }, processedChildren)

                return processedChildren.length > 0 ? processedChildren : null
              }
              const result = processNode(parent)
              return result
            }}
            emptyState={(level) => (
              <p className={clsx(level === 0 ? 'ml-0' : 'ml-2')}>
                {level === 0
                  ? (rootEmptyStateMessage ?? 'There are no changes.')
                  : 'No items.'}
              </p>
            )}
            level={0}
            defaultSelectFirst={defaultSelectFirst}
          >
            {Children}
          </TreePrimitive.TreeListChild>
        </TreePrimitive.TreeList>
      </TreePrimitive.Tree>
    </BlockIconStoreProvider>
  )
}
